import { ParseEQL, ParseExpression } from '@aavaz-ai/eql-parser-ts';
import {
  EQLFunction as ParsedEQLFunction,
  FilterQueryOperation as ParsedFilterQueryOperation,
  FilterUnion as ParsedFilterUnion,
  GroupBy as ParsedGroupBy,
  metadataFilter as ParsedMetadataFilter,
  OrderBy as ParsedOrderBy,
  sourceFilter as ParsedSourceFilter,
  themeFilter as ParsedThemeFilter,
  Timerange as ParsedTimeRange,
  Timezone as ParsedTimezone,
  Unit as ParsedUnit,
  WhereClause as ParsedWhereClause,
  Query,
} from '@aavaz-ai/eql-parser-ts/dist/types';
import { getFixedWhereClause } from '@modules/common/utils';
import { dayjsTz } from 'v2/core/date/date';
import {
  AggregatePeriod,
  GroupBy,
  TimeRange,
  TimeUnit,
} from 'v2/core/entities';
import { SortBy } from 'v2/core/entities/feedback/sortBy';
import { SourceDetail } from 'v2/core/entities/feedback/sources';
import { SourceName, isStringOfTypeSourceName } from 'v2/core/entities/fields';
import { Timezone } from 'v2/core/entities/user';
import {
  OperatorEquals,
  OperatorIsAnyOf,
  OperatorIsGreaterThan,
  OperatorIsLessThan,
  OperatorIsNotAnyOf,
  OperatorNotEquals,
} from 'v2/core/services/queryBuilder/operators';
import {
  FilterQueryOperation,
  FilterUnion,
  MetadataFilter,
  SourceFilter,
  WhereClause,
  labelHierarchyFilter,
} from 'v2/core/services/queryBuilder/whereClause';
import {
  revertSpecialCharacters,
  unescapeSingleDoubleQuotesAndBackslash,
} from '../edge-server/utils/unescapeSingleDoubleQuotesAndBackslash';
import {
  EQLFunction,
  EQLFunctionName,
  EQLProps,
  ExpressionType,
} from './types';

const toExpressionType = (
  expressionType: Query['expressionType'],
): ExpressionType => {
  switch (expressionType) {
    case 'records':
      return ExpressionType.Records;
    case 'aggregates':
      return ExpressionType.Aggregates;
  }
};

const toEQLFunctions = (functions?: ParsedEQLFunction[]): EQLFunction[] => {
  if (!functions) {
    return [];
  }

  return functions.map(
    (parsedFn): EQLFunction => ({
      name: toEQLFunctionName(parsedFn.name),
      params: parsedFn.params,
    }),
  );
};

const toEQLFunctionName = (
  parsedName: ParsedEQLFunction['name'],
): EQLFunctionName => {
  switch (parsedName) {
    case 'funccountfor':
      return EQLFunctionName.CountFor;
    case 'funcsentimentscore':
      return EQLFunctionName.SentimentScore;
    case 'funccountforpredictedon':
      return EQLFunctionName.CountForPredictedOn;
    case 'funccountforlabels':
      return EQLFunctionName.CountForLabels;
    case 'funccountformetadatavalues':
      return EQLFunctionName.CountForMetadataValues;
    case 'funccountforlanguages':
      return EQLFunctionName.CountForLanguages;
    case 'functop':
      return EQLFunctionName.Top;
    case 'funcanomaly':
      return EQLFunctionName.Anomaly;
    case 'funcapplytaxonomy':
      return EQLFunctionName.ApplyTaxonomy;
    case 'funcdeltatop':
      return EQLFunctionName.DeltaTop;
    default:
      return EQLFunctionName.Count;
  }
};

const toSourceFilter = (
  filter: ParsedSourceFilter,
  reversedSourceDetailsMap: Map<string, SourceDetail>,
): SourceFilter => {
  return {
    sources: filter.sources.map((source) => {
      const [sourceName, ...namespaceTokens] = source.split('-');
      const namespace = namespaceTokens.join('-');
      const detail = reversedSourceDetailsMap.get(sourceName);

      // if not found in map, if type is SourceName (ex. "FILE_UPLOAD"), not eqlValue string (ex. "FileUpload")
      // and we can directly use it as name field for source filter
      if (!detail && isStringOfTypeSourceName(sourceName)) {
        return {
          name: sourceName,
          namespace: namespace,
        };
      }

      if (!detail) {
        throw Error(`unknown source ${sourceName}`);
      }

      return {
        name: detail.name.name,
        namespace: namespace,
      };
    }),
  };
};

const mapNumericMetadataFilterOperator = (
  parsedOperator: ParsedMetadataFilter['operator'],
  exclude: boolean | undefined,
): MetadataFilter['operator'] => {
  if (exclude) {
    return OperatorNotEquals;
  }

  switch (parsedOperator) {
    case '<':
      return OperatorIsLessThan;
    case '>':
      return OperatorIsGreaterThan;
    case '=':
      return OperatorEquals;
    default:
      return undefined;
  }
};

const getMetadataFilterOperator = (
  parsedOperator: ParsedMetadataFilter['operator'],
  type: ParsedMetadataFilter['type'],
  exclude: boolean | undefined,
): MetadataFilter['operator'] => {
  if (type === 'number') {
    return mapNumericMetadataFilterOperator(parsedOperator, exclude);
  }

  if (exclude) {
    return OperatorIsNotAnyOf;
  }

  return parsedOperator === '=' ? OperatorIsAnyOf : undefined;
};

const toMetadataFilter = (
  filter: ParsedMetadataFilter,
  exclude?: boolean,
): MetadataFilter => {
  return {
    ...filter,
    key: revertSpecialCharacters(filter.key),
    values:
      filter.type === 'string'
        ? filter.values.map(revertSpecialCharacters)
        : filter.values,
    operator: getMetadataFilterOperator(filter.operator, filter.type, exclude),
  } as MetadataFilter;
};

const toLabelHierarchyFilter = (
  filter: ParsedThemeFilter,
): labelHierarchyFilter => ({
  nodeIDs: filter.themeIDs,
});

const toFilter = (
  filter: ParsedFilterUnion,
  reversedSourceDetailsMap: Map<string, SourceDetail>,
  exclude?: boolean,
): FilterUnion => ({
  ...filter,
  labelHierarchyFilter: filter.themeFilter
    ? toLabelHierarchyFilter(filter.themeFilter)
    : undefined,
  sourceFilter: filter.sourceFilter
    ? toSourceFilter(filter.sourceFilter, reversedSourceDetailsMap)
    : undefined,
  metadataFilter: filter.metadataFilter
    ? toMetadataFilter(filter.metadataFilter, exclude)
    : undefined,
});

const toOp = (
  op: ParsedFilterQueryOperation,
  reversedSourceDetailsMap: Map<string, SourceDetail>,
): FilterQueryOperation => ({
  type: op.type,
  params: op.params.map((where) =>
    toWhereClause(where, reversedSourceDetailsMap),
  ),
});

const toWhereClause = (
  where: ParsedWhereClause,
  reversedSourceDetailsMap: Map<string, SourceDetail>,
): WhereClause => {
  if (where.filter) {
    return {
      exclude: where.exclude,
      filter: toFilter(where.filter, reversedSourceDetailsMap, where.exclude),
    };
  }
  if (where.op) {
    return {
      exclude: where.exclude,
      op: toOp(where.op, reversedSourceDetailsMap),
    };
  }
  throw Error('atleast one of filter or op must be defined');
};

const toGroupBy = (groupBy: ParsedGroupBy): GroupBy => ({
  entity: groupBy.entity,
  reason: groupBy.reason,
  entitySentiment: groupBy.entitySentiment,
  source: groupBy.source,
  language: groupBy.language,
  recordSentiment: groupBy.sentiment,
  // though eql supports groupBy on multiple metadata, we render chart with
  // group by on only one field
  metadata:
    groupBy.metadata && groupBy.metadata.length > 0
      ? groupBy.metadata[0]
      : undefined,
  predictedOn: groupBy.predictedOn,
});

const toDateRange = (
  timerange: ParsedTimeRange,
  timezone: Timezone,
): TimeRange => {
  if (timerange.sinceOrAbsolute && timerange.sinceOrAbsolute.to) {
    return {
      type: 'absolute',
      startAfter: dayjsTz.unix(timerange.sinceOrAbsolute.from).tz(),
      endBefore: dayjsTz.unix(timerange.sinceOrAbsolute.to).tz(),
    };
  }
  if (timerange.sinceOrAbsolute && timerange.sinceOrAbsolute.since) {
    return {
      type: 'since',
      startAfter: dayjsTz.unix(timerange.sinceOrAbsolute.from).tz(),
    };
  }
  if (timerange.relative) {
    return {
      type: 'last',
      unit: toUnit(timerange.relative.unit),
      value: timerange.relative.delta,
    };
  }
  throw Error('invalid time range in eql');
};

const toSortBy = (orderBy: ParsedOrderBy[]): SortBy => {
  // though eql supports order by on multiple dimensions, we render chart with
  // sortBy on only one dimension
  const sortBy = orderBy[0];
  return {
    timestamp: sortBy.time,
    source: sortBy.source,
    metadataKey: sortBy.metadata,
    isAsc: sortBy.isAsc,
  };
};

const toAggregatePeriod = (unit: ParsedUnit): AggregatePeriod => {
  switch (unit) {
    case 'd':
    case 'day':
      return AggregatePeriod.DAY;
    case 'w':
    case 'week':
      return AggregatePeriod.WEEK;
    case 'm':
    case 'month':
      return AggregatePeriod.MONTH;
    case 'q':
    case 'quarter':
      return AggregatePeriod.QUARTER;
    case 'y':
    case 'year':
      return AggregatePeriod.YEAR;
  }
  throw Error(`unsupported aggregate period: ${unit}`);
};

const toUnit = (unit: ParsedUnit): TimeUnit => {
  switch (unit) {
    case 'd':
    case 'day':
      return TimeUnit.Day;
    case 'w':
    case 'week':
      return TimeUnit.Week;
    case 'm':
    case 'month':
      return TimeUnit.Month;
    case 'q':
    case 'quarter':
      return TimeUnit.Quarter;
    case 'y':
    case 'year':
      return TimeUnit.Year;
  }
  throw Error(`unsupported aggregate period: ${unit}`);
};

const toAggregatePeriodFromGroupBy = (
  groupBy?: ParsedGroupBy,
): AggregatePeriod | undefined => {
  if (groupBy?.aggregationWindow) {
    return toAggregatePeriod(groupBy.aggregationWindow.unit);
  }
  if (groupBy?.aggregationCalendarWindow) {
    return toAggregatePeriod(groupBy.aggregationCalendarWindow.unit);
  }
};

const toTimezone = (timezone?: ParsedTimezone): Timezone | undefined => {
  if (timezone?.ianaID) {
    return timezone.ianaID;
  }
  if (timezone?.iso8603Offset) {
    return timezone.iso8603Offset;
  }
};

export type ParsedEQLProps = Omit<EQLProps, 'sourceDetailsMap'>;

const getReversedSourceDetailMap = (
  sourceDetailsMap: Map<SourceName, SourceDetail>,
) => {
  return new Map(
    [...sourceDetailsMap.values()].map((detail) => [detail.eqlValue, detail]),
  );
};

export const parseEQL = (
  eql: string,
  sourceDetailsMap: Map<SourceName, SourceDetail>,
  timezone: Timezone,
): ParsedEQLProps => {
  const query = ParseEQL(unescapeSingleDoubleQuotesAndBackslash(eql));

  const reversedSourceDetailsMap = getReversedSourceDetailMap(sourceDetailsMap);
  const functions = toEQLFunctions(query.functions);

  return {
    expressionType: toExpressionType(query.expressionType),
    whereClause: query.where
      ? getFixedWhereClause(
          toWhereClause(query.where, reversedSourceDetailsMap),
        )
      : undefined,
    dateRange: query.timerange
      ? toDateRange(query.timerange, timezone)
      : undefined,
    groupBy: query.groupBy ? toGroupBy(query.groupBy) : undefined,
    sortBy: query.orderBy ? toSortBy(query.orderBy) : undefined,
    aggregatePeriod: toAggregatePeriodFromGroupBy(query.groupBy),
    timezone: toTimezone(query?.groupBy?.aggregationCalendarWindow?.timezone),
    taxonomyID: query.taxonomyID,
    functions,
  };
};

export const parseExpression = (
  expression: string,
  sourceDetailsMap: Map<SourceName, SourceDetail>,
): WhereClause => {
  const where = ParseExpression(expression);
  const reversedSourceDetailsMap = getReversedSourceDetailMap(sourceDetailsMap);
  return getFixedWhereClause(toWhereClause(where, reversedSourceDetailsMap));
};
